import {
  addDays,
  differenceInCalendarDays,
  isBefore,
  isEqual,
  isWithinInterval,
} from 'date-fns'
import humanizeString from 'humanize-string'

import {
  formatAbbreviatedMoney,
  formatDate,
  getDatesFromPeriod,
  parseISODate,
} from '@avantstay/backoffice-core'

import { PeriodSectionKind } from '@new/__global/components/TapeChart/PeriodSection/PeriodSection.types'
import {
  Period,
  PeriodKind,
} from '@new/__global/components/TapeChart/TapeChart.types'

export interface BlockedDatesByHomeId {
  [homeId: string]: string[]
}

export const getPeriodTitle = (
  period: Period,
  isRevenueTapeChart?: boolean,
): string | undefined => {
  const daysOfPeriod = differenceInCalendarDays(
    parseISODate(period.endDate),
    parseISODate(period.startDate),
  )
  const maintenanceTitle = daysOfPeriod === 1 ? 'M' : 'Maintenance'

  if (period.kind === PeriodKind.booking && !isRevenueTapeChart) {
    return period.guestName
  }

  if (period.kind === PeriodKind.block) {
    if (period.reason) {
      return period.reason === 'MAINTENANCE' && daysOfPeriod === 1
        ? 'M'
        : humanizeString(period.reason)
    }
    return maintenanceTitle
  }

  if (isRevenueTapeChart) {
    return formatAbbreviatedMoney(period?.folio?.summary?.total)
  }

  return undefined
}

const now = new Date()

export const getPeriodRendererType = (period: Period): PeriodSectionKind => {
  if (period.kind === PeriodKind.block) {
    if (period.parent?.id !== undefined) {
      return PeriodSectionKind.childBlock
    }

    return PeriodSectionKind.block
  }

  if (period.kind === PeriodKind.backToBack) {
    return PeriodSectionKind.backToBack
  }

  if (period.kind === PeriodKind.booking) {
    if (isBefore(parseISODate(period.endDate), now)) {
      return PeriodSectionKind.checkedOut
    }

    if (
      isWithinInterval(now, {
        start: parseISODate(period.startDate),
        end: parseISODate(period.endDate),
      })
    ) {
      return PeriodSectionKind.checkedIn
    }

    return PeriodSectionKind.booked
  }

  return PeriodSectionKind.generic
}

export const getAvailableDatesInfo = (date: string | Date, availableDates?: string) => {
  const { fromDate, toDate } = getDatesFromPeriod(availableDates)

  return {
    isOnAvailableDates: isWithinInterval(parseISODate(date), {
      start: parseISODate(fromDate),
      end: parseISODate(toDate),
    }),
    isOnLeftEdge: isEqual(parseISODate(date), new Date(fromDate.replace('-', '/'))),
    isOnRightEdge: isEqual(parseISODate(date), new Date(toDate.replace('-', '/'))),
  }
}

export const getPeriodRangeFromDates = (date: string) => {
  const [fromDate, toDate] = date.split('_')

  const addOneMoreDayForRange = isEqual(parseISODate(fromDate), parseISODate(toDate))
    ? addDays(parseISODate(toDate), 1)
    : toDate

  return { from: fromDate, to: formatDate(addOneMoreDayForRange) }
}
